import React, { createContext, useCallback, useContext, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { ReactComponent as LogoZl } from '../logo.svg';
import { INTRO_LOGO_MAX_WIDTH } from './intro/introHandoffLayout';
import { BUTTON, COLORS, SPACING, TYPO } from '../theme/designTokens';
import {
  grantSiteAccess,
  isPasswordGateEnabled,
  isSiteAccessGranted,
  verifySitePassword,
} from '../utils/siteAccess';

const SiteAccessContext = createContext(true);

export function useSeoAllowed() {
  return useContext(SiteAccessContext);
}

const passwordFieldSx = {
  ...TYPO.bodyLight,
  borderBottom: `1px solid ${COLORS.btnBorderLight}`,
  pb: '12px',
  pt: '4px',
  fontSize: '15px',
  '& input': {
    color: COLORS.text,
    padding: 0,
  },
  '& input::placeholder': {
    color: COLORS.textMuted,
    opacity: 1,
  },
};

interface SitePasswordGateProps {
  children: React.ReactNode;
}

const SitePasswordGate: React.FC<SitePasswordGateProps> = ({ children }) => {
  const gateEnabled = isPasswordGateEnabled();
  const [unlocked, setUnlocked] = useState(() => !gateEnabled || isSiteAccessGranted());
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const seoAllowed = !gateEnabled || unlocked;

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!verifySitePassword(password)) {
        setError(true);
        return;
      }
      grantSiteAccess();
      setError(false);
      setUnlocked(true);
    },
    [password],
  );

  if (unlocked) {
    return (
      <SiteAccessContext.Provider value={seoAllowed}>{children}</SiteAccessContext.Provider>
    );
  }

  return (
    <SiteAccessContext.Provider value={false}>
    <Box
      sx={{
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COLORS.bg,
        px: SPACING.sectionPaddingX,
        py: '48px',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: INTRO_LOGO_MAX_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: INTRO_LOGO_MAX_WIDTH,
            px: 3,
            boxSizing: 'border-box',
            mb: { xs: '40px', sm: '48px' },
            '& svg': { width: '100%', height: 'auto', display: 'block' },
          }}
        >
          <LogoZl aria-label="Zöllner Lorenz" />
        </Box>

        <Typography
          sx={{
            ...TYPO.eyebrow,
            color: COLORS.text,
            mb: '16px',
          }}
        >
          Geschützter Bereich
        </Typography>

        <Typography
          sx={{
            ...TYPO.bodyLight,
            mb: { xs: '32px', sm: '40px' },
            maxWidth: 340,
          }}
        >
          Bitte geben Sie das Passwort ein, um die Website zu öffnen.
        </Typography>

        <TextField
          type="password"
          variant="standard"
          placeholder="Passwort"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (error) setError(false);
          }}
          fullWidth
          autoFocus
          autoComplete="current-password"
          slotProps={{
            input: {
              disableUnderline: true,
              sx: passwordFieldSx,
            },
          }}
          sx={{ mb: error ? '12px' : '28px' }}
        />

        {error && (
          <Typography
            sx={{
              ...TYPO.bodyLight,
              color: COLORS.textMuted,
              fontSize: '13px',
              mb: '20px',
              width: '100%',
              textAlign: 'left',
            }}
          >
            Passwort nicht korrekt. Bitte erneut versuchen.
          </Typography>
        )}

        <Button type="submit" sx={{ ...BUTTON.light, width: 150, minWidth: 150 }}>
          Zugang
        </Button>
      </Box>
    </Box>
    </SiteAccessContext.Provider>
  );
};

export default SitePasswordGate;
